import { supabase } from "@/integrations/supabase/client";

// Static mapping for common city to country conversions
export const staticCityToCountryMapping: Record<string, string> = {
  // Indonésie
  'bali': 'Indonésie',
  'jakarta': 'Indonésie',
  'surabaya': 'Indonésie',
  'bandung': 'Indonésie',
  
  // France
  'paris': 'France',
  'marseille': 'France',
  'lyon': 'France',
  'toulouse': 'France',
  'nice': 'France',
  'nantes': 'France',
  'montpellier': 'France',
  'strasbourg': 'France',
  'bordeaux': 'France',
  'lille': 'France',
  
  // Portugal
  'lisbon': 'Portugal',
  'lisboa': 'Portugal',
  'porto': 'Portugal',
  'coimbra': 'Portugal',
  'braga': 'Portugal',
  
  // Espagne
  'madrid': 'Espagne',
  'barcelona': 'Espagne',
  'valencia': 'Espagne',
  'sevilla': 'Espagne',
  'bilbao': 'Espagne',
  
  // Maroc
  'casablanca': 'Maroc',
  'rabat': 'Maroc',
  'marrakech': 'Maroc',
  'fez': 'Maroc',
  
  // Autres pays populaires
  'london': 'Royaume-Uni',
  'berlin': 'Allemagne',
  'rome': 'Italie',
  'amsterdam': 'Pays-Bas',
  'brussels': 'Belgique',
  'zurich': 'Suisse',
  'geneva': 'Suisse',
  'vienna': 'Autriche',
  'budapest': 'Hongrie',
  'prague': 'République tchèque',
  'warsaw': 'Pologne',
  'stockholm': 'Suède',
  'copenhagen': 'Danemark',
  'oslo': 'Norvège',
  'helsinki': 'Finlande',
};

/**
 * Convert a city name to its corresponding country name
 * Uses static mapping first, then falls back to Google Places API
 */
export async function convertCityToCountry(cityOrCountry: string): Promise<string> {
  if (!cityOrCountry || cityOrCountry.trim() === '') {
    return 'Non spécifié';
  }

  const normalized = cityOrCountry.toLowerCase().trim();
  
  // First check static mapping
  const staticResult = staticCityToCountryMapping[normalized];
  if (staticResult) {
    return staticResult;
  }

  // If it's already a country name (longer than typical city names), return as-is
  if (isLikelyCountryName(cityOrCountry)) {
    return cityOrCountry;
  }

  try {
    // Call the edge function for Google Places API conversion
    const { data, error } = await supabase.functions.invoke('get-country-from-city', {
      body: { city: cityOrCountry }
    });

    if (error) {
      console.error('Error calling get-country-from-city:', error);
      return cityOrCountry; // Return original if conversion fails
    }

    return data?.country || cityOrCountry;
  } catch (error) {
    console.error('Error converting city to country:', error);
    return cityOrCountry; // Return original if conversion fails
  }
}

/**
 * Check if a location name is likely a country rather than a city
 */
function isLikelyCountryName(location: string): boolean {
  const countryKeywords = [
    'france', 'espagne', 'portugal', 'italie', 'allemagne', 'royaume-uni',
    'pays-bas', 'belgique', 'suisse', 'autriche', 'pologne', 'république',
    'états-unis', 'canada', 'mexique', 'brésil', 'argentine',
    'japon', 'corée', 'chine', 'inde', 'thaïlande', 'indonésie',
    'australie', 'nouvelle-zélande', 'afrique du sud', 'maroc', 'égypte'
  ];
  
  const normalized = location.toLowerCase();
  return countryKeywords.some(keyword => normalized.includes(keyword)) || 
         location.length > 15; // Typically countries have longer names than cities
}

/**
 * Convert multiple city names to countries in batch
 */
export async function convertCitiesToCountries(cities: string[]): Promise<Record<string, string>> {
  const results: Record<string, string> = {};
  
  // Process in parallel for efficiency
  const conversions = await Promise.all(
    cities.map(async (city) => ({
      original: city,
      country: await convertCityToCountry(city)
    }))
  );
  
  conversions.forEach(({ original, country }) => {
    results[original] = country;
  });
  
  return results;
}